import { isClient } from "@vueuse/core";
import { HttpMethodEnum } from "#shared/enums";
import type {
  HttpMethod,
  ApiOptions,
  ClientSideApiResponse,
} from "#shared/types/fetch";
import { useProfileStore } from "~~/stores/auth/profile";

export const useNuxtServerApi = () => {
  const { showSuccessToast } = useNotification();
  const { activeProfile } = useProfileStore();
  const request = async <TResponse = unknown, TBody = unknown>(
    method: HttpMethod,
    endpoint: string,
    options: ApiOptions<TBody> = {},
  ): Promise<ClientSideApiResponse<TResponse>> => {
    const { $csrfFetch } = useNuxtApp();
    const config = useRuntimeConfig();
    const defaultBaseUrl = config.public.nuxtServerEndpoint;

    const {
      query,
      body,
      headers: requestHeaders = {},
      timeoutMs = 10000,
      baseUrlOverride,
      toast: toastOption = false,
    } = options;

    const response: ClientSideApiResponse<TResponse> = {
      data: null,
      message: "",
      status: "pending",
    };

    try {
      const cleanEndpoint = normalizeEndpoint(endpoint);
      let url: string;

      if (isClient) {
        url = `/api/${cleanEndpoint}`;
      } else {
        const headers = useRequestHeaders(["host"]);
        const protocol = import.meta.dev ? "http" : "https";
        const origin = `${protocol}://${headers.host}`;
        url = `${origin}/api/${cleanEndpoint}`;
      }

      const forwardHeaders: Record<string, string> = {};
      if (!isClient) {
        const ssrHeaders = useRequestHeaders(["cookie"]);
        if (ssrHeaders.cookie) {
          forwardHeaders.cookie = ssrHeaders.cookie;
        }
      }

      // 2. Extract active profile ID from the store
      const activeProfileId = activeProfile?.id;

      const fetchOptions: Record<string, any> = {
        method,
        query,
        body: body as unknown as BodyInit,
        headers: {
          ...forwardHeaders,
          ...requestHeaders,
          // 3. Inject the identifier automatically whenever it exists
          ...(activeProfileId ? { "X-Active-Profile": activeProfileId } : {}),
        },
      };

      const result = await $csrfFetch<TResponse>(url, fetchOptions);

      response.data = result;
      response.status = "success";

      if (method !== HttpMethodEnum.GET && toastOption) {
        const msg =
          typeof toastOption === "object"
            ? (toastOption.success ?? "Request successful")
            : "Request successful";
        showSuccessToast("Success", msg);
      }
    } catch (err) {
      response.status = "error";
      console.error(
        `[API Wrapper Error] Failed on ${method} ${endpoint}:`,
        err,
      );
      handleError(err, `${method} ${endpoint}`);
    }

    return response;
  };

  return {
    get: <TResponse = unknown>(endpoint: string, options?: ApiOptions) =>
      request<TResponse>(HttpMethodEnum.GET, endpoint, options),
    post: <TResponse = unknown, TBody = unknown>(
      endpoint: string,
      body?: TBody,
      options?: ApiOptions<TBody>,
    ) =>
      request<TResponse, TBody>(HttpMethodEnum.POST, endpoint, {
        ...options,
        body,
      }),
    put: <TResponse = unknown, TBody = unknown>(
      endpoint: string,
      body?: TBody,
      options?: ApiOptions<TBody>,
    ) =>
      request<TResponse, TBody>(HttpMethodEnum.PUT, endpoint, {
        ...options,
        body,
      }),
    patch: <TResponse = unknown, TBody = unknown>(
      endpoint: string,
      body?: TBody,
      options?: ApiOptions<TBody>,
    ) =>
      request<TResponse, TBody>(HttpMethodEnum.PATCH, endpoint, {
        ...options,
        body,
      }),
    delete: <TResponse = unknown>(endpoint: string, options?: ApiOptions) =>
      request<TResponse>(HttpMethodEnum.DELETE, endpoint, options),
    concurrent: async <TResponses extends unknown[]>(
      calls: (() => Promise<ClientSideApiResponse<unknown>>)[],
    ): Promise<TResponses> => (await Promise.all(calls)) as TResponses,
  };
};

function normalizeEndpoint(endpoint: string) {
  return endpoint
    .replace(/^\/?api\/?/, "") // remove api or /api
    .replace(/^\/+/, ""); // remove leading slashes
}
